import React, { useState } from 'react';
import { useTranslations } from '../../locales/useTranslations';
import FadeInAnimation from '../Common/FadeInAnimation';
import { BiChevronLeft, BiChevronRight, BiX, BiZoomIn, BiZoomOut, BiReset } from 'react-icons/bi';

const WooCommerceSchnittstelleGallery = () => {
    const { t } = useTranslations();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const galleryItems = [
        {
            image: '/images/woocommerce/WooCommerce - Einstellung Konfiguration.webp',
            title: t('woocommerceSchnittstelle_gallery_item1_title'),
            description: t('woocommerceSchnittstelle_gallery_item1_desc')
        },
        {
            image: '/images/woocommerce/WooCommerce - Konfiguration Events.webp',
            title: t('woocommerceSchnittstelle_gallery_item2_title'),
            description: t('woocommerceSchnittstelle_gallery_item2_desc')
        },
        {
            image: '/images/woocommerce/WooCommerce - Konfiguration Datei Upload.webp',
            title: t('woocommerceSchnittstelle_gallery_item3_title'),
            description: t('woocommerceSchnittstelle_gallery_item3_desc')
        },
        {
            image: '/images/woocommerce/WooCommerce - Webhook Logs.webp',
            title: t('woocommerceSchnittstelle_gallery_item4_title'),
            description: t('woocommerceSchnittstelle_gallery_item4_desc')
        },
        {
            image: '/images/woocommerce/WooCommerce - XML Dateien.webp',
            title: t('woocommerceSchnittstelle_gallery_item5_title'),
            description: t('woocommerceSchnittstelle_gallery_item5_desc')
        },
        {
            image: '/images/woocommerce/WooCommerce - Fehler Seite.webp',
            title: t('woocommerceSchnittstelle_gallery_item6_title'),
            description: t('woocommerceSchnittstelle_gallery_item6_desc')
        }
    ];

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setLightboxOpen(true);
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
        document.body.style.overflow = 'auto';
    };

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const zoomIn = (e) => {
        e.stopPropagation();
        setZoomLevel((prev) => Math.min(prev + 0.5, 4));
    };

    const zoomOut = (e) => {
        e.stopPropagation();
        setZoomLevel((prev) => {
            const newZoom = Math.max(prev - 0.5, 1);
            if (newZoom === 1) {
                setImagePosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    const resetZoom = (e) => {
        e.stopPropagation();
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (zoomLevel > 1) {
            e.preventDefault();
            setIsDragging(true);
            setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoomLevel > 1) {
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            setZoomLevel((prev) => Math.min(prev + 0.25, 4));
        } else {
            setZoomLevel((prev) => {
                const newZoom = Math.max(prev - 0.25, 1);
                if (newZoom === 1) {
                    setImagePosition({ x: 0, y: 0 });
                }
                return newZoom;
            });
        }
    };

    return (
        <section className="wc-gallery pt-100 pb-100">
            <div className="container">
                <div className="row mb-5">
                    <div className="col-12 text-center">
                        <FadeInAnimation direction="up" delay={50}>
                            <span className="wc-section-badge">
                                {t('woocommerceSchnittstelle_gallery_badge')}
                            </span>
                            <h2 className="wc-section-title">
                                {t('woocommerceSchnittstelle_gallery_title')}
                            </h2>
                            <p className="wc-section-subtitle">
                                {t('woocommerceSchnittstelle_gallery_subtitle')}
                            </p>
                        </FadeInAnimation>
                    </div>
                </div>

                <div className="row">
                    {galleryItems.map((item, index) => (
                        <div key={index} className="col-lg-4 col-md-6 mb-4">
                            <FadeInAnimation direction="up" delay={100 + (index * 50)}>
                                <div 
                                    className="wc-gallery__item"
                                    onClick={() => openLightbox(index)}
                                >
                                    <div className="wc-gallery__image-wrapper">
                                        <img 
                                            src={item.image} 
                                            alt={item.title}
                                            className="wc-gallery__image"
                                            onError={(e) => {
                                                // Verhindere Endlosschleife bei fehlendem Placeholder
                                                e.target.onerror = null;
                                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="220" viewBox="0 0 400 220"%3E%3Crect fill="%23f0f0f0" width="400" height="220"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EBild wird geladen...%3C/text%3E%3C/svg%3E';
                                            }}
                                        />
                                        <div className="wc-gallery__overlay">
                                            <BiZoomIn className="wc-gallery__zoom-icon" />
                                        </div>
                                    </div>
                                    <div className="wc-gallery__info">
                                        <h3 className="wc-gallery__title">{item.title}</h3>
                                        <p className="wc-gallery__description">{item.description}</p>
                                    </div>
                                </div>
                            </FadeInAnimation>
                        </div>
                    ))}
                </div>
            </div>

            {/* Lightbox */}
            {lightboxOpen && (
                <div 
                    className="wc-gallery__lightbox" 
                    onClick={closeLightbox}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <button className="wc-gallery__lightbox-close" onClick={closeLightbox}>
                        <BiX />
                    </button>

                    {/* Zoom Controls */}
                    <div className="wc-gallery__zoom-controls" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="wc-gallery__zoom-btn" 
                            onClick={zoomIn}
                            disabled={zoomLevel >= 4}
                            title="Vergrößern"
                        >
                            <BiZoomIn />
                        </button>
                        <span className="wc-gallery__zoom-level">{Math.round(zoomLevel * 100)}%</span>
                        <button 
                            className="wc-gallery__zoom-btn" 
                            onClick={zoomOut}
                            disabled={zoomLevel <= 1}
                            title="Verkleinern"
                        >
                            <BiZoomOut />
                        </button>
                        <button 
                            className="wc-gallery__zoom-btn" 
                            onClick={resetZoom}
                            disabled={zoomLevel === 1}
                            title="Zurücksetzen"
                        >
                            <BiReset />
                        </button>
                    </div>
                    
                    <button 
                        className="wc-gallery__lightbox-nav wc-gallery__lightbox-nav--prev"
                        onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                    >
                        <BiChevronLeft />
                    </button>

                    <div 
                        className="wc-gallery__lightbox-content" 
                        onClick={(e) => e.stopPropagation()}
                        onWheel={handleWheel}
                    >
                        <div 
                            className="wc-gallery__lightbox-image-wrapper"
                            onMouseDown={handleMouseDown}
                            style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                        >
                            <img 
                                src={galleryItems[currentIndex].image} 
                                alt={galleryItems[currentIndex].title}
                                className="wc-gallery__lightbox-image"
                                style={{
                                    transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                                    transition: isDragging ? 'none' : 'transform 0.2s ease'
                                }}
                                draggable={false}
                            />
                        </div>
                        <div className="wc-gallery__lightbox-info">
                            <h3>{galleryItems[currentIndex].title}</h3>
                            <p>{galleryItems[currentIndex].description}</p>
                            <span className="wc-gallery__lightbox-counter">
                                {currentIndex + 1} / {galleryItems.length}
                            </span>
                        </div>
                    </div>

                    <button 
                        className="wc-gallery__lightbox-nav wc-gallery__lightbox-nav--next"
                        onClick={(e) => { e.stopPropagation(); goToNext(); }}
                    >
                        <BiChevronRight />
                    </button>
                </div>
            )}
        </section>
    );
};

export default WooCommerceSchnittstelleGallery;
